import React, { memo, useEffect, useRef } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaNumber from "../../../../ui-kit/kuika-number/kuika-number";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";

// ── Types ───────────────────────────────────────────────────────────
// Matches the response shape of Backend's /v1/api/Payment/ai-balance.
// Credits are enforced in every environment — there is no longer an
// "unlimited" short-circuit on the wire.

export interface BalanceInfo {
  creditsRemaining: number;
  creditsConsumed: number;
  totalPurchasedCents: number;
  totalGrantedCredits?: number;
  totalSpentUsd: number;
  starterCreditsIncluded: number;
}

export interface McpChatBalancePanelProps {
  isOpen: boolean;
  onToggle: () => void;
  balance: BalanceInfo;
  onGoToTopUp?: () => void;
}

type BalanceTone = "ok" | "warn" | "danger" | "empty";

interface BadgeMeta {
  label: string;
  className: string;
}

// ── Formatters ──────────────────────────────────────────────────────

/**
 * Whole credits, never below zero. The count is drawn by `KuikaNumber`, whose default format is
 * `en-US` grouping — the same on every machine locale — and whose changed digits roll when a
 * `balance_state` push lands while the panel is open.
 */
function wholeCredits(credits: number): number {
  return Math.floor(Math.max(0, credits));
}

function getUsagePercent(total: number, consumed: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((consumed / total) * 100)));
}

function getBalanceTone(credits: number): BalanceTone {
  if (credits <= 0) return "empty";
  if (credits < 20) return "danger";
  if (credits < 100) return "warn";
  return "ok";
}

function getBadgeMeta(tone: BalanceTone): BadgeMeta {
  switch (tone) {
    case "empty":
      return { label: "Empty", className: "mcp-balance-panel__badge--danger" };
    case "danger":
    case "warn":
      return { label: "Low", className: "mcp-balance-panel__badge--warn" };
    default:
      return { label: "Active", className: "mcp-balance-panel__badge--ok" };
  }
}

// ── Component ───────────────────────────────────────────────────────

const McpChatBalancePanel: React.FC<McpChatBalancePanelProps> = ({ isOpen, onToggle, balance, onGoToTopUp }) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      const pill = (target as Element).closest?.(".mcp-chat__balance-pill");
      if (pill) return;
      onToggle();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onToggle]);

  if (!isOpen) return null;

  const { creditsRemaining, creditsConsumed } = balance;
  const tone: BalanceTone = getBalanceTone(creditsRemaining);
  const badge: BadgeMeta = getBadgeMeta(tone);
  // Self-consistent total: remaining + consumed. Keeps the progress bar and the
  // two stat cards in agreement (unlike mixing purchased-cents with credits).
  const totalCredits: number = Math.max(0, creditsRemaining) + Math.max(0, creditsConsumed);
  const percent: number = getUsagePercent(totalCredits, creditsConsumed);
  const isCritical: boolean = tone === "empty" || tone === "danger";
  const progressClass = `mcp-balance-panel__progress-bar${isCritical ? " mcp-balance-panel__progress-bar--danger" : ""}`;

  return (
    <div className="mcp-balance-panel" ref={popoverRef}>
      <div className="mcp-balance-panel__header">
        <span className="mcp-balance-panel__header-icon" aria-hidden="true">
          <KuikaSvg name="bolt" type="common" width={16} height={16} color="var(--mcp-purple)" />
        </span>
        <div className="mcp-balance-panel__header-text">
          <span className="mcp-balance-panel__title">AI Chatbot Credits</span>
          <span className="mcp-balance-panel__subtitle">Pay as you go</span>
        </div>
        <span className={`mcp-balance-panel__badge ${badge.className}`}>
          <span className="mcp-balance-panel__badge-dot" />
          {badge.label}
        </span>
      </div>

      <div className="mcp-balance-panel__hero">
        <KuikaNumber
          className="mcp-balance-panel__hero-value"
          value={wholeCredits(creditsRemaining)}
          data-testid="mcpBalancePanelRemaining"
        />
        <span className="mcp-balance-panel__hero-label">credits remaining</span>
      </div>

      <div className="mcp-balance-panel__progress">
        <div className="mcp-balance-panel__progress-track">
          <div className={progressClass} style={{ width: `${percent}%` }} />
        </div>
        <div className="mcp-balance-panel__progress-meta">
          <span>
            <strong>{percent}%</strong> used this cycle
          </span>
          <span className="mcp-balance-panel__progress-total">
            <KuikaNumber value={wholeCredits(totalCredits)} /> total
          </span>
        </div>
      </div>

      <div className="mcp-balance-panel__stats">
        <div className="mcp-balance-panel__stat">
          <span className="mcp-balance-panel__stat-label">Consumed</span>
          <KuikaNumber className="mcp-balance-panel__stat-value" value={wholeCredits(creditsConsumed)} />
        </div>
        <div className="mcp-balance-panel__stat">
          <span className="mcp-balance-panel__stat-label">Total credits</span>
          <KuikaNumber className="mcp-balance-panel__stat-value" value={wholeCredits(totalCredits)} />
        </div>
      </div>

      {tone === "empty" && (
        <div className="mcp-balance-panel__warning mcp-balance-panel__warning--danger">
          You've run out of credits. Top up to continue using the AI chatbot.
        </div>
      )}
      {tone === "danger" && (
        <div className="mcp-balance-panel__warning mcp-balance-panel__warning--danger">
          Running low — less than 20 credits remaining.
        </div>
      )}

      {onGoToTopUp && (
        <div className="mcp-balance-panel__actions">
          <KuikaButton type="primary" size="md" onClick={onGoToTopUp} className="mcp-balance-panel__buy-btn">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {tone === "empty" ? "Top up to continue" : "Buy more credits"}
          </KuikaButton>
          <button type="button" className="mcp-balance-panel__usage-link" onClick={onGoToTopUp}>
            View usage
          </button>
        </div>
      )}
    </div>
  );
};

export default memo(McpChatBalancePanel);
